import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../firebase';

// ─── Blog Posts (Public) ───
export async function getBlogPosts({ category, searchQuery, limitCount = 50 } = {}) {
  let q = query(collection(db, 'blogPosts'), orderBy('publishedDate', 'desc'));

  if (category) {
    q = query(collection(db, 'blogPosts'), where('category', '==', category), orderBy('publishedDate', 'desc'));
  }

  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  const snap = await getDocs(q);
  let posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (searchQuery) {
    const lower = searchQuery.toLowerCase();
    posts = posts.filter(
      (p) =>
        p.title?.toLowerCase().includes(lower) ||
        p.excerpt?.toLowerCase().includes(lower) ||
        p.category?.toLowerCase().includes(lower)
    );
  }

  return posts;
}

export async function getBlogPostBySlug(slug) {
  const q = query(collection(db, 'blogPosts'), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function getRelatedPosts(currentId, count = 3) {
  const q = query(collection(db, 'blogPosts'), orderBy('publishedDate', 'desc'), limit(count + 1));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.id !== currentId)
    .slice(0, count);
}

export async function getBlogCategories() {
  const posts = await getBlogPosts();
  const cats = [...new Set(posts.map((p) => p.category).filter(Boolean))];
  return cats;
}

// ─── Blog Posts (Admin CRUD) ───
export async function createBlogPost(data) {
  return addDoc(collection(db, 'blogPosts'), {
    ...data,
    publishedDate: data.publishedDate || serverTimestamp(),
    createdAt: serverTimestamp(),
  });
}

export async function getBlogPostById(id) {
  const snap = await getDoc(doc(db, 'blogPosts', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function updateBlogPost(id, data) {
  return updateDoc(doc(db, 'blogPosts', id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBlogPost(id) {
  return deleteDoc(doc(db, 'blogPosts', id));
}

// ─── Image Upload ───
export async function uploadImage(file, path = 'blog') {
  const safeName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const storageRef = ref(storage, `${path}/${safeName}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function deleteImage(url) {
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
  } catch (e) {
    // Image may already be deleted
  }
}

// ─── Contact Submissions (Public) ───
export async function submitContactForm(data) {
  return addDoc(collection(db, 'contactSubmissions'), {
    ...data,
    isRead: false,
    createdAt: serverTimestamp(),
  });
}

// ─── Contact Submissions (Admin) ───
export async function getContactSubmissions() {
  const q = query(collection(db, 'contactSubmissions'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function markContactAsRead(id) {
  return updateDoc(doc(db, 'contactSubmissions', id), { isRead: true });
}

export async function deleteContactSubmission(id) {
  return deleteDoc(doc(db, 'contactSubmissions', id));
}

// ─── Newsletter (Public) ───
export async function subscribeNewsletter(email) {
  const q = query(collection(db, 'newsletter'), where('email', '==', email));
  const snap = await getDocs(q);
  if (!snap.empty) {
    throw new Error('already_subscribed');
  }
  return addDoc(collection(db, 'newsletter'), {
    email,
    isActive: true,
    createdAt: serverTimestamp(),
  });
}

// ─── Newsletter (Admin) ───
export async function getNewsletterSubscribers() {
  const q = query(collection(db, 'newsletter'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function deleteNewsletterSubscriber(id) {
  return deleteDoc(doc(db, 'newsletter', id));
}

// ─── FAQ Items (Public) ───
export async function getFaqItems() {
  const q = query(collection(db, 'faqItems'), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── FAQ Items (Admin CRUD) ───
export async function createFaqItem(data) {
  return addDoc(collection(db, 'faqItems'), {
    ...data,
    createdAt: serverTimestamp(),
  });
}

export async function updateFaqItem(id, data) {
  return updateDoc(doc(db, 'faqItems', id), data);
}

export async function deleteFaqItem(id) {
  return deleteDoc(doc(db, 'faqItems', id));
}

// ─── Dashboard Stats ───
export async function getDashboardStats() {
  const [posts, contacts, subscribers, faqs] = await Promise.all([
    getDocs(collection(db, 'blogPosts')),
    getDocs(collection(db, 'contactSubmissions')),
    getDocs(collection(db, 'newsletter')),
    getDocs(collection(db, 'faqItems')),
  ]);

  const unreadContacts = contacts.docs.filter(d => !d.data().isRead).length;

  return {
    totalPosts: posts.size,
    totalContacts: contacts.size,
    unreadContacts,
    totalSubscribers: subscribers.size,
    totalFaqs: faqs.size,
  };
}
